import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Chat } from '../utils/chatStorage';
import { exportChatToCSV } from '../utils/csvExport';
import { getOrCreateUserId } from '../utils/userIdentity';
import { track } from '../utils/analytics';
import mgLogo from '../assets/mygate_logo.jpg';
import pkg from '../../package.json';
import './Sidebar.css';

const { version } = pkg;

const TICKET_TS_KEY = 'mira_last_ticket_ts';
const TICKET_EMAIL_KEY = 'mira_ticket_email';
const TICKET_NAME_KEY = 'mira_ticket_name';
const TICKET_COOLDOWN_MS = 60 * 60 * 1000;

const isTicketOnCooldown = () =>
  Date.now() - Number(localStorage.getItem(TICKET_TS_KEY) || 0) < TICKET_COOLDOWN_MS;

interface SidebarProps {
  chats: Chat[];
  currentChatId: string | null;
  onSelectChat: (chatId: string) => void;
  onNewChat: () => void;
  onDeleteChat: (chatId: string) => void;
  isOpen: boolean;
  onToggle: () => void;
  onTicketRaised?: (chatId: string, ticketId: string, email: string) => void;
  raiseTicketChatId?: string | null;
  onClearRaiseTicketChatId?: () => void;
}

const KebabMenu: React.FC<{
  chat: Chat;
  onDownload: () => void;
  onDelete: () => void;
  onRaiseTicket: () => void;
  onShare: () => void;
  canRaiseTicket: boolean;
}> = ({ chat, onDownload, onDelete, onRaiseTicket, onShare, canRaiseTicket }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="kebab-wrapper" ref={ref}>
      <button
        className="kebab-button"
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        title="More options"
      >
        <svg viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="5" r="1.5" />
          <circle cx="12" cy="12" r="1.5" />
          <circle cx="12" cy="19" r="1.5" />
        </svg>
      </button>
      {open && (
        <div className="kebab-menu">
          <button className="kebab-item" onClick={(e) => { e.stopPropagation(); onDownload(); setOpen(false); }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Download
          </button>
          <button className="kebab-item" onClick={(e) => { e.stopPropagation(); onShare(); setOpen(false); }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
            </svg>
            Share
          </button>
          {canRaiseTicket && (
            <button className="kebab-item" onClick={(e) => { e.stopPropagation(); onRaiseTicket(); setOpen(false); }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
              Raise a Ticket
            </button>
          )}
          <div className="kebab-divider" />
          <button className="kebab-item kebab-item-danger" onClick={(e) => { e.stopPropagation(); onDelete(); setOpen(false); }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
            Delete
          </button>
        </div>
      )}
    </div>
  );
};

const moduleColors: Record<string, string> = {
  resident: '#ea580c',
  admin: '#7c3aed',
  smart_devices: '#059669',
};

const moduleNames: Record<string, string> = {
  resident: 'Resident',
  admin: 'Admin',
  smart_devices: 'Devices',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Sidebar: React.FC<SidebarProps> = ({
  chats,
  currentChatId,
  onSelectChat,
  onNewChat,
  onDeleteChat,
  isOpen,
  onToggle,
  onTicketRaised,
  raiseTicketChatId,
  onClearRaiseTicketChatId,
}) => {
  const [hoveredChatId, setHoveredChatId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<{ chatId: string; title: string; timer: ReturnType<typeof setTimeout> } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [ticketChat, setTicketChat] = useState<Chat | null>(null);
  const [ticketEmail, setTicketEmail] = useState('');
  const [ticketEmailError, setTicketEmailError] = useState('');
  const [ticketName, setTicketName] = useState('');
  const [ticketNameError, setTicketNameError] = useState('');
  const [ticketLoading, setTicketLoading] = useState(false);
  const [ticketResult, setTicketResult] = useState<{ success: boolean; ticketId?: string; message?: string } | null>(null);
  const [ticketRateLimitError, setTicketRateLimitError] = useState<string | null>(null);
  const [canRaiseTicket, setCanRaiseTicket] = useState(() => !isTicketOnCooldown());

  const [shareLoading, setShareLoading] = useState(false);
  const [shareResult, setShareResult] = useState<{ url: string; notFound?: boolean } | null>(null);
  const [shareCopied, setShareCopied] = useState(false);

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString();
  };

  const handleDelete = (chat: Chat) => {
    if (pendingDelete) {
      clearTimeout(pendingDelete.timer);
      onDeleteChat(pendingDelete.chatId);
    }
    const timer = setTimeout(() => {
      onDeleteChat(chat.id);
      setPendingDelete(null);
    }, 5000);
    setPendingDelete({ chatId: chat.id, title: chat.title, timer });
  };

  const handleUndoDelete = () => {
    if (!pendingDelete) return;
    clearTimeout(pendingDelete.timer);
    setPendingDelete(null);
  };

  const handleDownload = (chat: Chat) => {
    exportChatToCSV(chat.messages, chat.title);
  };

  const handleShare = async (chat: Chat) => {
    setShareLoading(true);
    setShareResult(null);
    setShareCopied(false);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/share`,
        { user_id: getOrCreateUserId(), chat_id: chat.id },
        { headers: { 'Content-Type': 'application/json' }, timeout: 10000 }
      );
      setShareResult({ url: response.data.share_url });
      track('share_created');
    } catch (err: any) {
      const is404 = err?.response?.status === 404;
      setShareResult({ url: '', notFound: is404 });
    } finally {
      setShareLoading(false);
    }
  };

  const handleCopyShareLink = (url: string) => {
    navigator.clipboard.writeText(url).then(() => {
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    });
  };

  const openTicketModal = (chat: Chat) => {
    if (isTicketOnCooldown()) {
      const remaining = TICKET_COOLDOWN_MS - (Date.now() - Number(localStorage.getItem(TICKET_TS_KEY) || 0));
      const mins = Math.ceil(remaining / 60000);
      setTicketRateLimitError(`You can only raise one ticket per hour. Please try again in ${mins} minute${mins !== 1 ? 's' : ''}.`);
      setTicketChat(chat);
      return;
    }
    setTicketRateLimitError(null);
    setTicketChat(chat);
    setTicketEmail(localStorage.getItem(TICKET_EMAIL_KEY) || '');
    setTicketName(localStorage.getItem(TICKET_NAME_KEY) || '');
    setTicketEmailError('');
    setTicketNameError('');
    setTicketResult(null);
  };

  useEffect(() => {
    if (!raiseTicketChatId) return;
    const chat = chats.find(c => c.id === raiseTicketChatId);
    if (chat) openTicketModal(chat);
    if (onClearRaiseTicketChatId) onClearRaiseTicketChatId();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raiseTicketChatId]);

  const closeTicketModal = () => {
    setTicketChat(null);
    setTicketEmail('');
    setTicketEmailError('');
    setTicketName('');
    setTicketNameError('');
    setTicketResult(null);
    setTicketLoading(false);
    setTicketRateLimitError(null);
  };

  const handleRaiseTicket = async () => {
    if (!ticketChat) return;
    let hasError = false;
    if (!ticketName.trim()) {
      setTicketNameError('Please enter your name.');
      hasError = true;
    }
    if (!EMAIL_RE.test(ticketEmail.trim())) {
      setTicketEmailError('Please enter a valid email address.');
      hasError = true;
    }
    if (hasError) return;
    setTicketEmailError('');
    setTicketNameError('');
    setTicketLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/escalate`,
        { user_id: getOrCreateUserId(), chat_id: ticketChat.id, email: ticketEmail.trim().toLowerCase(), name: ticketName.trim() },
        { headers: { 'Content-Type': 'application/json' }, timeout: 15000 }
      );
      localStorage.setItem(TICKET_TS_KEY, String(Date.now()));
      localStorage.setItem(TICKET_EMAIL_KEY, ticketEmail.trim().toLowerCase());
      localStorage.setItem(TICKET_NAME_KEY, ticketName.trim());
      setCanRaiseTicket(false);
      setTicketResult({ success: true, ticketId: response.data.ticket_id });
      track('ticket_raised', { module: ticketChat.selectedModule ?? 'unknown' });
      if (onTicketRaised && ticketChat) {
        onTicketRaised(ticketChat.id, response.data.ticket_id, ticketEmail.trim().toLowerCase());
      }
    } catch {
      setTicketResult({ success: false, message: 'Failed to raise ticket. Please try again.' });
    } finally {
      setTicketLoading(false);
    }
  };

  return (
    <>
      <div className={`sidebar-overlay${isOpen ? ' sidebar-overlay-visible' : ''}`} onClick={onToggle} />

      <div className={`sidebar ${isOpen ? 'sidebar-open' : ''}`}>
        <div className="sidebar-header">
          <span className="sidebar-header-label">Chats</span>
          <button className="new-chat-icon-button" onClick={() => onNewChat()} title="New chat">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
            </svg>
            <span className="new-chat-label">New Chat</span>
          </button>
        </div>

        <div className="sidebar-search">
          <svg className="sidebar-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            className="sidebar-search-input"
            placeholder="Search chats"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="sidebar-search-clear" onClick={() => setSearchQuery('')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>

        <div className="sidebar-chats">
          {(() => {
            const filtered = chats.filter(c =>
              c.title.toLowerCase().includes(searchQuery.toLowerCase())
            );
            if (chats.length === 0) return (
              <div className="sidebar-empty">
                <p>No chats yet</p>
                <p className="sidebar-empty-hint">Start a new conversation</p>
              </div>
            );
            if (filtered.length === 0) return (
              <div className="sidebar-empty">
                <p>No results</p>
                <p className="sidebar-empty-hint">Try a different search</p>
              </div>
            );
            return filtered.map((chat) => {
              const moduleColor = chat.selectedModule ? moduleColors[chat.selectedModule] : undefined;
              const moduleName = chat.selectedModule ? moduleNames[chat.selectedModule] : undefined;
              const isActive = chat.id === currentChatId;
              const isHovered = hoveredChatId === chat.id;

              return (
                <div
                  key={chat.id}
                  className={`sidebar-chat-item ${isActive ? 'sidebar-chat-active' : ''}`}
                  onClick={() => onSelectChat(chat.id)}
                  onMouseEnter={() => setHoveredChatId(chat.id)}
                  onMouseLeave={() => setHoveredChatId(null)}
                >
                  {moduleColor && (
                    <span className="sidebar-module-dot" style={{ background: moduleColor }} title={moduleName} />
                  )}
                  <div className="sidebar-chat-info">
                    <div className="sidebar-chat-title">{chat.title}</div>
                    <div className="sidebar-chat-date">{formatDate(chat.updatedAt)}</div>
                  </div>
                  {(isHovered || isActive) && (
                    <KebabMenu
                      chat={chat}
                      onDownload={() => handleDownload(chat)}
                      onDelete={() => handleDelete(chat)}
                      onRaiseTicket={() => openTicketModal(chat)}
                      onShare={() => handleShare(chat)}
                      canRaiseTicket={!!chat.raiseTicket}
                    />
                  )}
                </div>
              );
            });
          })()}
        </div>

        <div className="sidebar-footer">
          <div className="sidebar-footer-brand">
            <div className="sidebar-footer-brand-logo">
              <img src={mgLogo} alt="Mygate" />
            </div>
            <span className="sidebar-footer-brand-text">Mygate</span>
          </div>
          <span className="sidebar-footer-version">v{version}</span>
        </div>
      </div>

      <button className="sidebar-toggle" onClick={onToggle}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          {isOpen
            ? <line x1="18" y1="6" x2="6" y2="18" />
            : <><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="18" x2="21" y2="18" /></>
          }
        </svg>
      </button>

      {pendingDelete && (
        <div className="delete-toast">
          <span>"{pendingDelete.title}" will be deleted</span>
          <button onClick={handleUndoDelete} className="delete-toast-undo">Undo</button>
        </div>
      )}

      {(shareLoading || shareResult !== null) && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 400
        }}>
          <div style={{
            background: '#fff', borderRadius: '14px', padding: '28px 32px',
            maxWidth: '420px', width: '90%', boxShadow: '0 12px 40px rgba(0,0,0,0.2)'
          }}>
            {shareLoading ? (
              <p style={{ margin: 0, textAlign: 'center', fontSize: '0.875rem', color: '#6b7280' }}>
                Generating share link...
              </p>
            ) : shareResult?.url ? (
              <>
                <h3 style={{ margin: '0 0 6px', fontSize: '0.9375rem', fontWeight: 700, color: '#111827' }}>
                  Share this chat
                </h3>
                <p style={{ margin: '0 0 16px', fontSize: '0.8125rem', color: '#6b7280', lineHeight: 1.5 }}>
                  Anyone with this link can view and continue the conversation. Link expires in 30 days.
                </p>
                <div style={{
                  display: 'flex', gap: '8px', alignItems: 'center',
                  background: '#f3f4f6', borderRadius: '8px', padding: '10px 12px',
                  marginBottom: '20px'
                }}>
                  <span style={{
                    flex: 1, fontSize: '0.8125rem', color: '#374151',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                  }}>
                    {shareResult.url}
                  </span>
                  <button
                    onClick={() => handleCopyShareLink(shareResult.url)}
                    style={{
                      flexShrink: 0, padding: '6px 12px', borderRadius: '6px', border: 'none',
                      background: shareCopied ? '#059669' : 'var(--mg-yellow)',
                      color: shareCopied ? '#fff' : '#111827',
                      fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer',
                      transition: 'background 0.2s'
                    }}
                  >
                    {shareCopied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => setShareResult(null)}
                    style={{
                      padding: '8px 18px', borderRadius: '7px', border: '1px solid #e5e7eb',
                      background: '#f9fafb', color: '#374151', fontSize: '0.8125rem',
                      cursor: 'pointer', fontWeight: 500
                    }}
                  >
                    Close
                  </button>
                </div>
              </>
            ) : (
              <>
                <p style={{ margin: '0 0 20px', fontSize: '0.875rem', color: '#dc2626', textAlign: 'center' }}>
                  {shareResult?.notFound
                    ? 'This chat cannot be shared yet. Send a message first, then try again.'
                    : 'Failed to generate share link. Please try again.'}
                </p>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => setShareResult(null)}
                    style={{
                      padding: '8px 18px', borderRadius: '7px', border: '1px solid #e5e7eb',
                      background: '#f9fafb', color: '#374151', fontSize: '0.8125rem',
                      cursor: 'pointer', fontWeight: 500
                    }}
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {ticketChat && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 400
        }}>
          <div style={{
            background: '#fff', borderRadius: '14px', padding: '28px 32px',
            maxWidth: '400px', width: '90%', boxShadow: '0 12px 40px rgba(0,0,0,0.2)'
          }}>
            {ticketRateLimitError ? (
              <>
                <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                </div>
                <h3 style={{ margin: '0 0 12px', fontSize: '0.9375rem', fontWeight: 700, color: '#111827', textAlign: 'center' }}>
                  Too Many Requests
                </h3>
                <p style={{ margin: '0 0 24px', fontSize: '0.844rem', color: '#6b7280', textAlign: 'center', lineHeight: 1.6 }}>
                  {ticketRateLimitError}
                </p>
                <button onClick={closeTicketModal} style={{
                  width: '100%', padding: '10px', borderRadius: '8px', border: 'none',
                  background: 'var(--mg-yellow)', color: '#111827', fontSize: '0.875rem',
                  fontWeight: 600, cursor: 'pointer'
                }}>OK</button>
              </>
            ) : ticketResult ? (
              ticketResult.success ? (
                <>
                  <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                      <polyline points="22 4 12 14.01 9 11.01" />
                    </svg>
                  </div>
                  <h3 style={{ margin: '0 0 8px', fontSize: '0.9375rem', fontWeight: 700, color: '#111827', textAlign: 'center' }}>
                    Ticket Raised Successfully
                  </h3>
                  <p style={{ margin: '0 0 4px', fontSize: '0.844rem', color: '#6b7280', textAlign: 'center' }}>
                    Your ticket ID is
                  </p>
                  <p style={{ margin: '0 0 24px', fontSize: '1rem', fontWeight: 700, color: '#111827', textAlign: 'center', letterSpacing: '0.04em' }}>
                    {ticketResult.ticketId}
                  </p>
                  <p style={{ margin: '0 0 24px', fontSize: '0.8125rem', color: '#6b7280', textAlign: 'center' }}>
                    Our team will reach out to <strong>{ticketEmail}</strong> shortly.
                  </p>
                  <button onClick={closeTicketModal} style={{
                    width: '100%', padding: '10px', borderRadius: '8px', border: 'none',
                    background: 'var(--mg-yellow)', color: '#111827', fontSize: '0.875rem',
                    fontWeight: 600, cursor: 'pointer'
                  }}>Done</button>
                </>
              ) : (
                <>
                  <h3 style={{ margin: '0 0 12px', fontSize: '0.9375rem', fontWeight: 700, color: '#111827' }}>
                    Something went wrong
                  </h3>
                  <p style={{ margin: '0 0 24px', fontSize: '0.844rem', color: '#dc2626' }}>
                    {ticketResult.message}
                  </p>
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button onClick={closeTicketModal} style={{
                      padding: '8px 18px', borderRadius: '7px', border: '1px solid #e5e7eb',
                      background: '#f9fafb', color: '#374151', fontSize: '0.8125rem', cursor: 'pointer', fontWeight: 500
                    }}>Cancel</button>
                    <button onClick={handleRaiseTicket} style={{
                      padding: '8px 18px', borderRadius: '7px', border: 'none',
                      background: 'var(--mg-yellow)', color: '#111827', fontSize: '0.8125rem', cursor: 'pointer', fontWeight: 600
                    }}>Retry</button>
                  </div>
                </>
              )
            ) : (
              <>
                <h3 style={{ margin: '0 0 6px', fontSize: '0.9375rem', fontWeight: 700, color: '#111827' }}>
                  Raise a ticket with Mygate Support
                </h3>
                <p style={{ margin: '0 0 20px', fontSize: '0.8125rem', color: '#6b7280', lineHeight: 1.5 }}>
                  Chat: <em>{ticketChat.title}</em>
                </p>
                <input
                  type="text"
                  placeholder="Your name"
                  value={ticketName}
                  onChange={(e) => { setTicketName(e.target.value); setTicketNameError(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleRaiseTicket(); }}
                  autoFocus
                  style={{
                    width: '100%', padding: '10px 14px', borderRadius: '8px',
                    border: `1.5px solid ${ticketNameError ? '#dc2626' : '#e5e7eb'}`,
                    fontSize: '0.875rem', color: '#111827', outline: 'none',
                    boxSizing: 'border-box', marginBottom: ticketNameError ? '6px' : '16px'
                  }}
                />
                {ticketNameError && (
                  <p style={{ margin: '0 0 12px', fontSize: '0.75rem', color: '#dc2626' }}>
                    {ticketNameError}
                  </p>
                )}
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={ticketEmail}
                  onChange={(e) => { setTicketEmail(e.target.value); setTicketEmailError(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleRaiseTicket(); }}
                  style={{
                    width: '100%', padding: '10px 14px', borderRadius: '8px',
                    border: `1.5px solid ${ticketEmailError ? '#dc2626' : '#e5e7eb'}`,
                    fontSize: '0.875rem', color: '#111827', outline: 'none',
                    boxSizing: 'border-box', marginBottom: ticketEmailError ? '6px' : '20px'
                  }}
                />
                {ticketEmailError && (
                  <p style={{ margin: '0 0 16px', fontSize: '0.75rem', color: '#dc2626' }}>
                    {ticketEmailError}
                  </p>
                )}
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button onClick={closeTicketModal} disabled={ticketLoading} style={{
                    padding: '8px 18px', borderRadius: '7px', border: '1px solid #e5e7eb',
                    background: '#f9fafb', color: '#374151', fontSize: '0.8125rem', cursor: 'pointer', fontWeight: 500
                  }}>Cancel</button>
                  <button onClick={handleRaiseTicket} disabled={ticketLoading} style={{
                    padding: '8px 18px', borderRadius: '7px', border: 'none',
                    background: 'var(--mg-yellow)', color: '#111827', fontSize: '0.8125rem',
                    cursor: ticketLoading ? 'not-allowed' : 'pointer', fontWeight: 600, opacity: ticketLoading ? 0.7 : 1
                  }}>
                    {ticketLoading ? 'Raising...' : 'Raise Ticket'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};


export default Sidebar;
